import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatTimestamp } from '../utils/timestampUtils';
import './TimestampEditor.css';

const TimestampDistributeDialog = ({
  selectedWords,
  onApply,
  onCancel
}) => {
  const [mode, setMode] = useState('even');
  const dialogRef = useRef(null);

  // Calculate range info
  const firstWord = selectedWords[0];
  const lastWord = selectedWords[selectedWords.length - 1];
  const rangeStart = firstWord?.start || 0;
  const rangeEnd = lastWord?.end || 0;
  const totalDuration = rangeEnd - rangeStart;
  const avgDuration = totalDuration / selectedWords.length;

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter') {
        handleApply();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, mode]);

  const handleApply = useCallback(() => {
    onApply(mode);
  }, [mode, onApply]);

  // Calculate preview for proportional mode
  const getProportionalPreview = () => {
    const totalChars = selectedWords.reduce((sum, w) => sum + (w.word?.length || 1), 0);
    const shortest = selectedWords.reduce((min, w) => {
      const chars = w.word?.length || 1;
      const duration = (chars / totalChars) * totalDuration;
      return duration < min ? duration : min;
    }, Infinity);
    const longest = selectedWords.reduce((max, w) => {
      const chars = w.word?.length || 1;
      const duration = (chars / totalChars) * totalDuration;
      return duration > max ? duration : max;
    }, 0);
    return { shortest, longest };
  };

  const proportionalPreview = getProportionalPreview();

  return createPortal(
    <>
      <div className="timestamp-dialog-backdrop" onClick={onCancel} />
      <div ref={dialogRef} className="timestamp-distribute-dialog">
        <div className="dialog-header">
          <span className="dialog-title">Distribute Timestamps</span>
          <button className="dialog-close" onClick={onCancel}>×</button>
        </div>

        <div className="dialog-body">
          <div className="dialog-info">
            Selected: {selectedWords.length} words
            <br />
            Range: {formatTimestamp(rangeStart)} - {formatTimestamp(rangeEnd)}
            <br />
            Total duration: {totalDuration.toFixed(3)}s
          </div>

          <div className="distribute-mode-selector">
            <button
              className={`distribute-mode-btn ${mode === 'even' ? 'active' : ''}`}
              onClick={() => setMode('even')}
            >
              Even
              <small>Equal duration per word</small>
            </button>
            <button
              className={`distribute-mode-btn ${mode === 'proportional' ? 'active' : ''}`}
              onClick={() => setMode('proportional')}
            >
              Proportional
              <small>By word length</small>
            </button>
          </div>

          <div className="dialog-preview">
            {mode === 'even' ? (
              <>Each word: {avgDuration.toFixed(3)}s</>
            ) : (
              <>
                Shortest: {proportionalPreview.shortest.toFixed(3)}s,
                Longest: {proportionalPreview.longest.toFixed(3)}s
              </>
            )}
          </div>
        </div>

        <div className="dialog-footer">
          <button className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="save-btn" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TimestampDistributeDialog;
